import React, { useState, useEffect } from 'react';
import { Calendar, Filter, ChevronDown, ChevronUp, X } from 'lucide-react';
import { getDefaultDateRange } from 'utils/helpers';

export interface DateRange {
  dateFrom?: string;
  dateTo?: string;
}

interface DateFilterProps {
  dateRange: DateRange;
  onChange: (dateRange: DateRange) => void;
  className?: string;
  compact?: boolean; // For inline compact mode
}

interface QuickRangeButton {
  label: string;
  shortLabel: string; // Short label for mobile
  days: number;
}

const QUICK_RANGES: QuickRangeButton[] = [
  { label: 'Yesterday', shortLabel: 'Yest', days: 0 },
  { label: 'Today', shortLabel: 'Today', days: 1 },
  { label: '3 Days', shortLabel: '3D', days: 3 },
  { label: '5 Days', shortLabel: '5D', days: 5 },
  { label: '7 Days', shortLabel: '7D', days: 7 },
  { label: '10 Days', shortLabel: '10D', days: 10 },
  { label: '15 Days', shortLabel: '15D', days: 15 },
  { label: '30 Days', shortLabel: '30D', days: 30 }
];

const DateFilter: React.FC<DateFilterProps> = ({ dateRange, onChange, className = '', compact = false }) => {
  // Local state to hold temporary date range before applying
  const [localDateRange, setLocalDateRange] = useState<DateRange>(dateRange);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeQuickRange, setActiveQuickRange] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false); // For mobile collapsed view
  const [showCustomRange, setShowCustomRange] = useState(false); // Show/hide custom date inputs

  // Update local state when parent dateRange changes
  useEffect(() => {
    setLocalDateRange(dateRange);
    setHasChanges(false);
  }, [dateRange]);

  // Helper to get date range for last N days (including today) or yesterday
  // Uses LOCAL timezone to ensure dates match user's local calendar
  // Special case: days = 0 means "Yesterday only"
  const getDateRangeForDays = (days: number): DateRange => {
    const today = new Date();
    const startDate = new Date();
    const endDate = new Date();

    if (days === 0) {
      // Yesterday: from yesterday 00:00:00 to yesterday 23:59:59
      startDate.setDate(today.getDate() - 1);
      endDate.setDate(today.getDate() - 1);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    } else {
      // Last N days including today
      startDate.setDate(today.getDate() - (days - 1)); // Last N days including today
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
    }

    // Format dates in local timezone (YYYY-MM-DDTHH:mm)
    const formatLocalDateTime = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    return {
      dateFrom: formatLocalDateTime(startDate),
      dateTo: formatLocalDateTime(endDate)
    };
  };

  // Check if current range matches a quick range
  const getActiveQuickRange = (range: DateRange): number | null => {
    if (!range.dateFrom || !range.dateTo) return null;

    const from = new Date(range.dateFrom);
    const to = new Date(range.dateTo);

    // Extract just the date part for comparison
    const fromDate = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const toDate = new Date(to.getFullYear(), to.getMonth(), to.getDate());

    // Get today and yesterday in local timezone (date only)
    const today = new Date();
    const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const yesterdayDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

    // Check if it's yesterday (from and to are both yesterday)
    if (fromDate.getTime() === yesterdayDate.getTime() && toDate.getTime() === yesterdayDate.getTime()) {
      return 0; // days: 0 represents Yesterday
    }

    const diffTime = Math.abs(toDate.getTime() - fromDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both days

    const isSameDay = toDate.getTime() === todayDate.getTime();

    if (isSameDay && QUICK_RANGES.find(qr => qr.days === diffDays)) {
      return diffDays;
    }

    return null;
  };

  // Update active quick range indicator when dates change
  useEffect(() => {
    setActiveQuickRange(getActiveQuickRange(localDateRange));
  }, [localDateRange]);

  const handleQuickRange = (days: number) => {
    const newRange = getDateRangeForDays(days);
    setLocalDateRange(newRange);
    setActiveQuickRange(days);
    setHasChanges(true); // Mark as changed but don't apply immediately
  };

  const handleDateFromChange = (value: string) => {
    setLocalDateRange({
      ...localDateRange,
      dateFrom: value || undefined
    });
    setHasChanges(true);
    setActiveQuickRange(null); // Clear quick range when manually editing
  };

  const handleDateToChange = (value: string) => {
    setLocalDateRange({
      ...localDateRange,
      dateTo: value || undefined
    });
    setHasChanges(true);
    setActiveQuickRange(null); // Clear quick range when manually editing
  };

  const handleApply = () => {
    onChange(localDateRange);
    setHasChanges(false);
  };

  const handleClear = () => {
    const defaultRange = getDefaultDateRange();
    setLocalDateRange(defaultRange);
    onChange(defaultRange);
    setHasChanges(false);
  };

  const hasDateFilter = localDateRange.dateFrom || localDateRange.dateTo;

  // Get display text for current range
  const getDisplayText = () => {
    if (!localDateRange.dateFrom || !localDateRange.dateTo) return 'Select date range';
    const fromDate = new Date(localDateRange.dateFrom);
    const toDate = new Date(localDateRange.dateTo);
    return `${fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow ${className}`}>
      {/* Mobile: Collapsed Header */}
      <div className="lg:hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between p-3 text-left"
        >
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-cyan-600" />
            <span className="font-medium text-gray-700 dark:text-gray-200 text-sm">
              {getDisplayText()}
            </span>
            {hasChanges && (
              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
            )}
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {/* Mobile: Expanded Content */}
        {isExpanded && (
          <div className="px-3 pb-3 border-t border-gray-100 dark:border-gray-700">
            {/* Quick Range - Horizontal Scroll */}
            <div className="py-3">
              <div className="flex gap-2 overflow-x-auto pb-2 -mx-3 px-3 scrollbar-hide">
                {QUICK_RANGES.map((range) => (
                  <button
                    key={range.days}
                    onClick={() => handleQuickRange(range.days)}
                    className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      activeQuickRange === range.days
                        ? 'bg-cyan-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {range.shortLabel}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Range Toggle */}
            <button
              onClick={() => setShowCustomRange(!showCustomRange)}
              className="text-xs text-cyan-600 dark:text-cyan-400 font-medium mb-2"
            >
              {showCustomRange ? 'Hide custom range' : 'Custom range'}
            </button>

            {/* Custom Date Inputs - Stacked on Mobile */}
            {showCustomRange && (
              <div className="space-y-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">From</label>
                  <input
                    type="datetime-local"
                    value={localDateRange.dateFrom || ''}
                    onChange={(e) => handleDateFromChange(e.target.value)}
                    max={localDateRange.dateTo || undefined}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">To</label>
                  <input
                    type="datetime-local"
                    value={localDateRange.dateTo || ''}
                    onChange={(e) => handleDateToChange(e.target.value)}
                    min={localDateRange.dateFrom || undefined}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>
            )}

            {/* Action Buttons - Full Width on Mobile */}
            <div className="flex gap-2">
              <button
                onClick={handleApply}
                disabled={!hasChanges}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                  hasChanges
                    ? 'bg-cyan-600 text-white active:bg-cyan-700'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                }`}
              >
                <Filter className="w-4 h-4" />
                Apply
              </button>
              {hasDateFilter && (
                <button
                  onClick={handleClear}
                  className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Desktop: Compact Inline View */}
      <div className="hidden lg:block p-3">
        {/* Quick Range Row */}
        <div className="flex items-end gap-3 flex-wrap">
          {/* Filter Icon & Label */}
          <div className="flex items-center gap-2 pb-2">
            <Calendar className="w-4 h-4 text-cyan-600" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Date Range</span>
          </div>

          {/* Quick Range Buttons - Inline */}
          <div className="flex items-center gap-1.5 pb-1">
            {QUICK_RANGES.map((range) => (
              <button
                key={range.days}
                onClick={() => handleQuickRange(range.days)}
                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                  activeQuickRange === range.days
                    ? 'bg-cyan-600 text-white shadow-sm'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {range.shortLabel}
              </button>
            ))}
          </div>

          {/* Separator */}
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>

          {/* Custom Date Inputs - Inline */}
          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">From</label>
            <input
              type="datetime-local"
              value={localDateRange.dateFrom || ''}
              onChange={(e) => handleDateFromChange(e.target.value)}
              max={localDateRange.dateTo || undefined}
              className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>

          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">To</label>
            <input
              type="datetime-local"
              value={localDateRange.dateTo || ''}
              onChange={(e) => handleDateToChange(e.target.value)}
              min={localDateRange.dateFrom || undefined}
              max={(() => {
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;
              })()}
              className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>

          {/* Action Buttons - Inline */}
          <div className="flex items-center gap-2 ml-auto pb-0.5">
            <button
              onClick={handleApply}
              disabled={!hasChanges}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                hasChanges
                  ? 'bg-cyan-600 text-white hover:bg-cyan-700 shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              Apply
            </button>

            {hasDateFilter && (
              <button
                onClick={handleClear}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md font-medium transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Status indicator */}
        {(hasChanges || (!hasChanges && hasDateFilter)) && (
          <div className="flex items-center justify-end mt-2">
            {hasChanges && (
              <span className="text-xs text-orange-600 dark:text-orange-400 font-medium flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                Unsaved changes
              </span>
            )}
            {!hasChanges && hasDateFilter && (
              <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                {getDisplayText()}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DateFilter;
